import {
  Box,
  Typography,
  Stack,
  TextField,
  Alert,
  IconButton
} from "@mui/material";
import { RemoveIcon } from "@qubit/autoparts";
import {
  FieldPathByValue,
  FieldValues,
  Path,
  useFormContext
} from "react-hook-form";

import { DistributionProfile } from "~/redux/warehouse/tools.openApi";

export const DistributionProfileField = <T extends FieldValues>({
  propertyName,
  index,
  removeItem
}: {
  propertyName: FieldPathByValue<T, DistributionProfile[] | undefined>;
  index: number;
  removeItem: (index: number) => void;
}) => {
  const propertyPath = `${propertyName}.${index}`;
  const formContext = useFormContext<T>();
  const countErrorMessage = formContext.getFieldState(
    `${propertyPath}.count` as Path<T>
  ).error?.message;
  const selectionWeightErrorMessage = formContext.getFieldState(
    `${propertyPath}.selectionWeight` as Path<T>
  ).error?.message;

  return (
    <Box py={1}>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        role="listitem"
        aria-label={propertyPath}
      >
        <TextField
          {...formContext.register(`${propertyPath}.count` as Path<T>, {
            required: { value: true, message: "Count is required" },
            min: { value: 1, message: "Count must be greater than zero" }
          })}
          sx={{ flexBasis: "75px" }}
          variant="standard"
          label="Count"
          type="number"
          error={!!countErrorMessage}
        />
        <TextField
          {...formContext.register(
            `${propertyPath}.selectionWeight` as Path<T>,
            {
              required: { value: true, message: "Weight is required" },
              min: { value: 0.1, message: "Weight must be greater than zero" }
            }
          )}
          slotProps={{ htmlInput: { step: 0.1 } }}
          sx={{ flexBasis: "75px" }}
          variant="standard"
          label="Weight"
          type="number"
          error={!!selectionWeightErrorMessage}
        />
        <IconButton aria-label="delete" onClick={() => removeItem(index)}>
          <RemoveIcon />
        </IconButton>
      </Stack>
      {(!!countErrorMessage || !!selectionWeightErrorMessage) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {!!countErrorMessage && <Typography>{countErrorMessage}</Typography>}
          {!!selectionWeightErrorMessage && (
            <Typography>{selectionWeightErrorMessage}</Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};
